import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { InvoiceModelV3 } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { ButtonProps } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../../hooks/use-scoped-tracking-ids';
import { useFinalizeStepTrackingCallbacks, useInitiateStepTracking } from '../../../../utils/step-tracking-store';
import { PAYMENT_MULTI_STEPS } from '../../../../utils/steps';
import { CreateStepNavigationProps } from '../create.types';

export const CreateGenericNavigation = ({ body: Body }: CreateStepNavigationProps) => {
  const { t } = useTranslation('payments');
  const {
    person,
    setActiveInvoiceId,
    onInvoiceCreated,
    multiStep: { goPrevStep, goNextStep, onCancel },
    activeGateway,
    flow,
    isFocused,
  } = useCollectPaymentMultiContext();

  const { completeStepTracking, cancelStepTracking, updateModalState } = useFinalizeStepTrackingCallbacks({
    step: 'create-invoice',
    variant: 'default',
  });

  const [isCreating, setIsCreating] = useState(false);

  const createGoBack = useCallback(() => {
    if (!!person) {
      return goPrevStep({ orStep: PAYMENT_MULTI_STEPS.select });
    }

    cancelStepTracking();

    console.warn('Multi Collect > Create Step > Go back clicked but no person found in context');
  }, [person, goPrevStep, cancelStepTracking]);

  const onCreateRequest = useCallback(
    (invoice: InvoiceModelV3) => {
      setActiveInvoiceId(invoice?.id);
      if (invoice) {
        onInvoiceCreated?.(invoice.id);
      }
      goNextStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
      completeStepTracking();
    },
    [onInvoiceCreated, goNextStep, setActiveInvoiceId, completeStepTracking]
  );

  const navigate = useNavigate();

  const redirectToInvoiceDetailPage = (invoiceId: string) => {
    navigate({ to: `/payments/invoices/${invoiceId}` });
  };

  /**
   * Create and close
   */
  const onClickSecondary = useCallback(
    async ({
      resetFields,
      handleCreateRequest,
    }: {
      resetFields: () => void;
      handleCreateRequest: () => Promise<InvoiceModelV3>;
    }) => {
      try {
        const invoice = await handleCreateRequest();
        resetFields();
        onInvoiceCreated?.(invoice.id);
        redirectToInvoiceDetailPage?.(invoice.id);
        updateModalState({ status: 'complete' });
        onCancel?.();
      } catch (error) {
        console.error('Error creating invoice:', error);
      }
    },
    [onCancel, onInvoiceCreated, updateModalState]
  );

  const { secondaryTrackingId } = useScopedTrackingIds({
    secondaryTrackingId: 'secondary-btn--create',
  });

  const secondary: ButtonProps = {
    trackingId: secondaryTrackingId,
    children: t('Create'),
    loading: isCreating,
  };

  useInitiateStepTracking(
    {
      activeGateway,
      variant: 'default',
      screenWidth: window.innerWidth,
      step: 'create-invoice',
      flow,
    },
    { enabled: isFocused }
  );

  useEffect(() => {
    return () => {
      /**
       * This should be used for any track state that can be background-click closed.
       * Note: payment method steps have the background click disabled.
       * Safe to potentially call again, because has an `alreadyFinished` check.
       */
      cancelStepTracking();
    };
  }, []);

  return (
    <Body
      onCreateRequest={onCreateRequest}
      onGoBack={createGoBack}
      onClickSecondary={onClickSecondary}
      secondary={secondary}
      isCreating={isCreating}
      setIsCreating={setIsCreating}
    />
  );
};
